import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Crown } from 'lucide-react';
import { PROVIDERS, getProviderFees } from '../utils/feeCalculator';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Currency, Provider } from '../types/calculator';
type RateCurrency = 'EUR' | 'USD';
export function TodayRates() {
  const [selectedCurrency, setSelectedCurrency] = useState<RateCurrency>('EUR');
  const {
    language
  } = useLanguage();
  const t = translations[language].todayRates;
  // Get display rates from the fee calculator (which uses Supabase data)
  const getDisplayRates = () => {
    const rates: Record<RateCurrency, Record<string, number>> = {
      EUR: {},
      USD: {}
    };
    Object.values(PROVIDERS).forEach(provider => {
      // Import the display rates from feeCalculator
      // We'll need to expose a function to get display rates
      const calculationRates = getCalculationRatesForDisplay();
      rates.EUR[provider.id] = calculationRates[provider.id]?.EUR || 0;
      rates.USD[provider.id] = calculationRates[provider.id]?.USD || 0;
    });
    return rates;
  };
  // Helper to get current rates (will use Supabase data if available)
  const getCalculationRatesForDisplay = () => {
    // This will be populated by the feeCalculator's current data
    // For now, we'll import it directly
    const rates: Record<string, Record<string, number>> = {};
    Object.values(PROVIDERS).forEach(provider => {
      // Call calculateFees to get current rates
      const eurRate = calculateFeesForRate(1, 'EUR', provider.id);
      const usdRate = calculateFeesForRate(1, 'USD', provider.id);
      rates[provider.id] = {
        EUR: eurRate.displayedFxRate,
        USD: usdRate.displayedFxRate
      };
    });
    return rates;
  };
  const rates = getDisplayRates();
  // Sort providers by rate (lowest/best first)
  const sortedProviders = Object.values(PROVIDERS).sort((a, b) => {
    const rateA = rates[selectedCurrency][a.id];
    const rateB = rates[selectedCurrency][b.id];
    return rateA - rateB;
  });
  // Find the best (lowest) rate
  const bestRate = Math.min(...sortedProviders.map(p => rates[selectedCurrency][p.id]));
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-16 sm:py-24 bg-gray-50" data-id="element-158">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-159">
        <div className="text-center mb-12" data-id="element-160">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-2xl mb-4" data-id="element-161">
            <TrendingUp className="w-8 h-8 text-white" data-id="element-162" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-163">
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-gray-600" data-id="element-164">{t.subtitle}</p>

          <div className="mt-6 inline-flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border border-gray-200" data-id="element-165">
            <button onClick={() => setSelectedCurrency('EUR')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCurrency === 'EUR' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-166">
              EUR
            </button>
            <button onClick={() => setSelectedCurrency('USD')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCurrency === 'USD' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-167">
              USD
            </button>
          </div>
        </div>

        <motion.div variants={container} initial="hidden" animate="show" className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-168">
          {sortedProviders.map(provider => {
          const rate = rates[selectedCurrency][provider.id];
          const isBestRate = rate === bestRate;
          return <motion.div key={provider.id} variants={item} className={`relative bg-white rounded-xl p-6 border-2 transition-all ${isBestRate ? 'border-emerald-500 shadow-lg' : 'border-gray-200'}`} data-id="element-169">
                {isBestRate && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap" data-id="element-170">
                    <Crown className="w-3 h-3" data-id="element-171" />
                    {t.bestRate}
                  </div>}

                <div className="flex items-center gap-3 mb-4" data-id="element-172">
                  <div className={`w-12 h-12 ${provider.bgColor} rounded-lg flex items-center justify-center overflow-hidden`} data-id="element-173">
                    {provider.logoUrl ? <img src={provider.logoUrl} alt={provider.name} className="w-8 h-8 object-contain" data-id="element-174" /> : <span className={`text-xl font-bold ${provider.color}`} data-id="element-175">
                        {provider.name.charAt(0)}
                      </span>}
                  </div>
                  <div data-id="element-176">
                    <h3 className="font-bold text-gray-900 text-sm" data-id="element-177">
                      {provider.name}
                    </h3>
                  </div>
                </div>

                <div className="text-center py-4" data-id="element-178">
                  <div className={`text-3xl font-bold ${isBestRate ? 'text-emerald-600' : 'text-gray-900'}`} data-id="element-179">
                    {rate.toLocaleString('fr-FR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 3
                })}
                  </div>
                  <div className="text-sm text-gray-500 mt-1" data-id="element-180">
                    XOF {t.perUnit} {selectedCurrency}
                  </div>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}
// Helper function to get rates from feeCalculator
function calculateFeesForRate(amount: number, currency: Currency, provider: Provider) {
  // Import calculateFees dynamically to avoid circular dependencies
  const {
    calculateFees
  } = require('../utils/feeCalculator');
  return calculateFees(amount, currency, provider);
}